import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaCalculator, FaCheck, FaCalendarAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { ui } from '../utils/styles';
import axios from 'axios';

const RenegociarModal = ({ show, onClose, devedor, dividasSelecionadas, onSave }) => {
    // --- ESTADOS ---
    const [juros, setJuros] = useState(0);
    const [novoTotal, setNovoTotal] = useState(0);
    const [numParcelas, setNumParcelas] = useState(1);
    const [dataPrimeiraParcela, setDataPrimeiraParcela] = useState('');
    const [parcelasPreview, setParcelasPreview] = useState([]);

    // --- CÁLCULOS INICIAIS ---
    const somaOriginal = useMemo(() => {
        return dividasSelecionadas.reduce((acc, d) => {
            const valor = typeof d.valor_original === 'string'
                ? parseFloat(d.valor_original.replace(',', '.'))
                : d.valor_original;
            return acc + (valor || 0);
        }, 0);
    }, [dividasSelecionadas]);

    // Resetar campos ao abrir
    useEffect(() => {
        if (show) {
            setJuros(0);
            setNovoTotal(somaOriginal);
            setNumParcelas(1);
            setParcelasPreview([]);
            const hoje = new Date().toISOString().split('T')[0];
            setDataPrimeiraParcela(hoje);
        }
    }, [show, somaOriginal]);

    // --- FUNÇÕES DE LÓGICA ---

    const calcularNovoTotal = () => {
        const valorJuros = somaOriginal * (juros / 100);
        setNovoTotal(somaOriginal + valorJuros);
        toast.success("Total recalculado com juros!");
    };

    const gerarParcelas = () => {
        if (!dataPrimeiraParcela) {
            toast.error("Informe a data do primeiro pagamento.");
            return;
        }

        const valorParcela = novoTotal / numParcelas;
        const novasParcelas = [];

        for (let i = 0; i < numParcelas; i++) {
            const dataVenc = new Date(dataPrimeiraParcela + 'T00:00:00');
            dataVenc.setMonth(dataVenc.getMonth() + i);

            novasParcelas.push({
                parcela: i + 1,
                vencimento: dataVenc.toISOString().split('T')[0],
                valor: valorParcela
            });
        }

        setParcelasPreview(novasParcelas);
    };

    const handleConfirmarAcordo = async () => {
        if (parcelasPreview.length === 0) {
            toast.error("Gere as parcelas antes de confirmar.");
            return;
        }

        // Pegamos o ID do cliente contratante da primeira dívida selecionada
        // Assumimos que o usuário selecionou dívidas de um mesmo credor para o acordo
        const idClienteContratante = dividasSelecionadas[0]?.id_cliente;

        if (!idClienteContratante) {
            toast.error("Erro: Não foi possível identificar o Cliente Contratante das dívidas.");
            return;
        }

        const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
        const authHeader = { Authorization: `Bearer ${localStorage.getItem('userToken')}` };
        const toastId = toast.loading('Processando renegociação e gerando parcelas...');

        try {
            // 1. CRIAR AS NOVAS PARCELAS NO BACKEND
            // Enviamos um objeto contendo os dados do devedor, o cliente dono da dívida e o array de parcelas
            const payloadAcordo = {
                id_devedor: devedor.id,
                id_cliente: idClienteContratante,
                parcelas: parcelasPreview.map(p => ({
                    valor: p.valor,
                    data_vencimento: p.vencimento,
                    observacao: `Parcela ${p.parcela}/${numParcelas} - Acordo Ref. Dívidas: ${dividasSelecionadas.map(d => d.id).join(', ')}`
                }))
            };

            // Rota sugerida: um POST que já cria as dívidas com status 'Em Acordo' ou 'Em Aberto'
            await axios.post(`${URL_BACKEND}/divida/gerar-acordo`, payloadAcordo, { headers: authHeader });

            // 2. BAIXAR AS DÍVIDAS ORIGINAIS
            // Agora que as novas foram criadas com sucesso, marcamos as antigas como 'Renegociado' ou 'Pago'
            const promessasBaixa = dividasSelecionadas.map(divida =>
                axios.put(`${URL_BACKEND}/divida/baixa/${divida.id}/Pago`,
                    { motivo_baixa: 'RENEGOCIACAO' },
                    { headers: authHeader }
                )
            );

            await Promise.all(promessasBaixa);

            toast.success("Acordo firmado e parcelas geradas com sucesso!", { id: toastId });

            onSave(); // Recarrega a lista de dívidas na tela principal
            onClose(); // Fecha o modal

        } catch (error) {
            console.error("Erro na renegociação:", error);
            const msg = error.response?.data?.message || "Erro ao processar o acordo.";
            toast.error(msg, { id: toastId });
        }
    };

    const formatCurrency = (val) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    if (!show) return null;

    return (
        <div style={ui.modalOverlay}>
            <div style={{ ...ui.modalContent, width: '700px', maxWidth: '90%' }}>

                {/* Header */}
                <div style={{ ...ui.modalHeader, justifyContent: 'space-between', display: 'flex' }}>
                    <h2 style={ui.modalTitle}>Renegociar Dívidas</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>
                        <FaTimes size={20} />
                    </button>
                </div>

                <div style={{ padding: '20px' }}>
                    <p style={{ marginBottom: 15 }}>Devedor: <strong>{devedor.nome}</strong></p>

                    {/* Resumo Seleção */}
                    <div style={{ backgroundColor: '#f3f4f6', padding: '10px', borderRadius: '8px', marginBottom: '20px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#666' }}>Soma das Dívidas Selecionadas ({dividasSelecionadas.length}):</span>
                        <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1f2937' }}>{formatCurrency(somaOriginal)}</div>
                    </div>

                    {/* Controles de Renegociação */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Juro Mensal (%)</label>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <input
                                    type="number"
                                    style={ui.input}
                                    value={juros}
                                    onChange={e => setJuros(parseFloat(e.target.value))}
                                />
                                <button onClick={calcularNovoTotal} style={{ ...ui.buttonPrimary, padding: '0 15px' }} title="Calcular Novo Total">
                                    <FaCalculator />
                                </button>
                            </div>
                        </div>

                        <div style={ui.formGroup}>
                            <label style={ui.label}>Novo Total do Acordo</label>
                            <input type="text" style={{ ...ui.input, fontWeight: 'bold', color: '#b91c1c' }} value={formatCurrency(novoTotal)} disabled />
                        </div>

                        <div style={ui.formGroup}>
                            <label style={ui.label}>Dividir em (Parcelas)</label>
                            <input
                                type="number"
                                style={ui.input}
                                value={numParcelas}
                                min="1"
                                onChange={e => setNumParcelas(parseInt(e.target.value))}
                            />
                        </div>

                        <div style={ui.formGroup}>
                            <label style={ui.label}>Data 1º Pagamento</label>
                            <input
                                type="date"
                                style={ui.input}
                                value={dataPrimeiraParcela}
                                onChange={e => setDataPrimeiraParcela(e.target.value)}
                            />
                        </div>
                    </div>

                    <button
                        onClick={gerarParcelas}
                        style={{ ...ui.buttonPrimary, width: '100%', background: '#0ea5e9', marginTop: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}
                    >
                        <FaCalendarAlt /> Gerar Parcelas
                    </button>

                    {/* Preview das Parcelas */}
                    {parcelasPreview.length > 0 && (
                        <div style={{ marginTop: 20, maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                                    <tr>
                                        <th style={{ ...ui.th, padding: '8px' }}>Parc.</th>
                                        <th style={{ ...ui.th, padding: '8px' }}>Vencimento</th>
                                        <th style={{ ...ui.th, padding: '8px' }}>Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parcelasPreview.map(p => (
                                        <tr key={p.parcela} style={ui.tr}>
                                            <td style={{ ...ui.tdCenter, padding: '8px' }}>{p.parcela}</td>
                                            <td style={{ ...ui.tdCenter, padding: '8px' }}>{new Date(p.vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                            <td style={{ ...ui.tdCenter, padding: '8px', fontWeight: 'bold' }}>{formatCurrency(p.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer Ações */}
                <div style={ui.modalFooter}>
                    <button onClick={onClose} style={ui.buttonSecondary}>Cancelar</button>
                    <button
                        onClick={handleConfirmarAcordo}
                        disabled={parcelasPreview.length === 0}
                        style={{ ...ui.buttonPrimary, background: parcelasPreview.length > 0 ? '#10b981' : '#ccc', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                        <FaCheck /> Confirmar Acordo
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RenegociarModal;